// ---------------------------------------------------------------------------
// CENTRAL ENTITY REGISTRY — the governed source of truth for the identity
// facts of Buddha KA (Person) and KAfrequency (Organization).
//
// Scope: IDENTITY FACTS ONLY. Release catalog, books, codex concepts, wisdom,
// activations and commerce keep their own sources; they are content, not
// identity. Everything that describes *who* Buddha KA is, or *what*
// KAfrequency is, must resolve here — so /about, /artist/buddha-ka, /press,
// /press/factsheet, JSON-LD, page metadata and the /data/*.json feeds can
// never silently diverge.
//
// Governance rules:
//   • Every fact carries a verification state and a provenance note.
//   • `lastModified` is the date the FACT changed — never deployment time.
//   • The database (`entity_registry`) may override a fact's value, provenance
//     and lastModified. If the DB is unreachable, these defaults render.
//   • Nothing here is inferred or generated. Owner-confirmed copy is verbatim.
// ---------------------------------------------------------------------------

import buddhaKaPortrait from "@/assets/buddha-ka-official.png.asset.json";
import { OFFICIAL_ARTIST_PROFILES } from "@/lib/content";
import { SITE_URL, absoluteUrl } from "@/lib/site";

/** How much confidence stands behind a fact. */
export type FactVerification =
  /** Confirmed directly by Buddha KA. */
  | "owner_confirmed"
  /** Derived from first-party repository/database data that is itself canonical. */
  | "first_party"
  /** Present but awaiting explicit owner confirmation. */
  | "pending_confirmation";

export interface EntityFact<T> {
  value: T;
  verified: FactVerification;
  /** Where the value came from. Human sentence, not a file path guess. */
  source: string;
  /** ISO date (YYYY-MM-DD) the underlying fact last actually changed. */
  lastModified: string;
}

const fact = <T,>(
  value: T,
  verified: FactVerification,
  source: string,
  lastModified: string,
): EntityFact<T> => ({ value, verified, source, lastModified });

/**
 * Date the consolidated identity facts were governed into this registry with
 * Buddha KA's confirmation. Facts authored earlier keep their own date when it
 * is genuinely known; this is not a deployment timestamp and does not move on
 * rebuild.
 */
export const REGISTRY_GOVERNED_ON = "2026-08-12";

/* ------------------------------------------------------------ canonical ids */

/**
 * Canonical @ids. These are load-bearing across the whole JSON-LD graph and
 * must never change — external knowledge graphs key off them.
 */
export const ENTITY_IDS = {
  person: `${SITE_URL}/#buddha-ka`,
  organization: `${SITE_URL}/#kafrequency`,
  website: `${SITE_URL}/#website`,
  logo: `${SITE_URL}/#logo`,
  imprint: `${SITE_URL}/#imprint`,
} as const;

/* --------------------------------------------------------------- the facts */

/**
 * The canonical one-line identity. Confirmed by Buddha KA on 2026-08-12.
 * Used where a single sentence must carry the whole entity — never repeated
 * verbatim on every page.
 */
export const CANONICAL_SHORT_DESCRIPTION =
  "Buddha KA is an independent artist, author, producer, and creator of KAfrequency, building music, spiritual technology, and systems for inner sovereignty.";

export interface EntityRoleSet {
  /** Roles in canonical display order. */
  display: string[];
  /** schema.org jobTitle values. */
  jobTitle: string[];
}

export const ENTITY_DEFAULTS = {
  "person.displayName": fact(
    "Buddha KA",
    "owner_confirmed",
    "Professional artist name used on every first-party release.",
    "2026-01-01",
  ),
  "person.legalName": fact(
    "Curtis Shores",
    "owner_confirmed",
    "Legal name and author byline, published in the approved press kit.",
    "2026-01-01",
  ),
  "person.shortDescription": fact(
    CANONICAL_SHORT_DESCRIPTION,
    "owner_confirmed",
    "Canonical one-line identity confirmed by Buddha KA.",
    REGISTRY_GOVERNED_ON,
  ),
  "person.roles": fact<EntityRoleSet>(
    {
      display: ["Independent recording artist", "Author", "Producer", "Visual world-builder", "Creator of KAfrequency"],
      jobTitle: ["Recording Artist", "Author", "Producer", "Founder of KAfrequency"],
    },
    "owner_confirmed",
    "Role set confirmed by Buddha KA, including the producer role.",
    REGISTRY_GOVERNED_ON,
  ),
  "person.birthPlace": fact(
    "Chicago, Illinois, USA",
    "owner_confirmed",
    "Stated in the approved long-form biography.",
    "2026-01-01",
  ),
  "person.portrait": fact(
    { url: absoluteUrl(buddhaKaPortrait.url), alt: "Buddha KA — official portrait" },
    "owner_confirmed",
    "Official portrait asset supplied by Buddha KA and stored first-party.",
    "2026-01-01",
  ),
  "person.website": fact(
    SITE_URL,
    "first_party",
    "Canonical website operated by KAfrequency.",
    "2026-01-01",
  ),
  "person.profiles": fact(
    OFFICIAL_ARTIST_PROFILES,
    "owner_confirmed",
    "Verified official profile registry in src/lib/content.ts; duplicates are excluded by the entity audit.",
    "2026-08-05",
  ),
  "press.email": fact(
    "Buddhaka9@gmail.com",
    "owner_confirmed",
    "Currently verified press address. A domain alias may replace it later; not switched until it exists.",
    "2026-01-01",
  ),
  "org.name": fact(
    "KAfrequency",
    "owner_confirmed",
    "Official ecosystem/brand name.",
    "2026-01-01",
  ),
  "org.definition": fact(
    "KAfrequency is the artist-owned creative ecosystem founded and architected by Buddha KA, uniting music, books, daily spoken activations, visual worlds, and consciousness systems under one canon, published first-party at buddhaka.com.",
    "owner_confirmed",
    "Derived verbatim from the approved press boilerplate.",
    "2026-01-01",
  ),
  "org.founderRelation": fact(
    "Buddha KA is the founder and creator of KAfrequency. KAfrequency is the ecosystem; Buddha KA is the person. They are distinct entities with distinct canonical @ids.",
    "owner_confirmed",
    "Entity-disambiguation rule confirmed by Buddha KA.",
    REGISTRY_GOVERNED_ON,
  ),
  "framework.name": fact(
    "KA Flow",
    "owner_confirmed",
    "Musical framework named in the approved press biographies.",
    "2026-01-01",
  ),
} as const;

export type EntityFactKey = keyof typeof ENTITY_DEFAULTS;

export const ENTITY_FACT_KEYS = Object.keys(ENTITY_DEFAULTS) as EntityFactKey[];

/** A registry snapshot: every governed fact, defaults merged with overrides. */
export type EntityRegistry = {
  [K in EntityFactKey]: EntityFact<(typeof ENTITY_DEFAULTS)[K]["value"]>;
};

/** Static fallback snapshot. Always safe, always renderable, never stale-empty. */
export const STATIC_ENTITY_REGISTRY = ENTITY_DEFAULTS as unknown as EntityRegistry;

/** Read a fact value from a snapshot (or the static defaults). */
export function factValue<K extends EntityFactKey>(
  key: K,
  registry: EntityRegistry = STATIC_ENTITY_REGISTRY,
): (typeof ENTITY_DEFAULTS)[K]["value"] {
  return (registry[key] ?? STATIC_ENTITY_REGISTRY[key]).value;
}

/**
 * The most recent genuine change across the registry — the entity's real
 * `dateModified`. Derived from persisted fact dates, never from build time.
 */
export function entityLastModified(registry: EntityRegistry = STATIC_ENTITY_REGISTRY): string {
  return ENTITY_FACT_KEYS.map((k) => (registry[k] ?? STATIC_ENTITY_REGISTRY[k]).lastModified)
    .sort()
    .at(-1)!;
}

/** Registry rows shaped for the public /data feeds (value + provenance). */
export function publicEntityFacts(registry: EntityRegistry = STATIC_ENTITY_REGISTRY) {
  return ENTITY_FACT_KEYS.map((key) => {
    const f = registry[key] ?? STATIC_ENTITY_REGISTRY[key];
    return { key, value: f.value, verified: f.verified, source: f.source, lastModified: f.lastModified };
  });
}

/**
 * Merge database overrides on top of the static defaults. Unknown keys are
 * ignored so a stray row can never introduce an ungoverned fact.
 */
export function mergeEntityOverrides(
  rows: Array<{ key: string; value: unknown; verified?: string | null; source_note?: string | null; last_modified?: string | null }>,
): EntityRegistry {
  const merged = { ...STATIC_ENTITY_REGISTRY } as Record<string, EntityFact<unknown>>;
  for (const row of rows) {
    if (!ENTITY_FACT_KEYS.includes(row.key as EntityFactKey)) continue;
    if (row.value === null || row.value === undefined) continue;
    const base = merged[row.key]!;
    merged[row.key] = {
      value: row.value,
      verified: (row.verified as FactVerification) ?? base.verified,
      source: row.source_note ?? base.source,
      lastModified: (row.last_modified ?? base.lastModified).slice(0, 10),
    };
  }
  return merged as EntityRegistry;
}
